import JSON5 from 'json5';

export interface JSONValidationResult {
  isValid: boolean;
  error?: string;
  parsed?: any;
  line?: number;
  column?: number;
  suggestion?: string;
}

export interface HighlightRange {
  start: number;
  end: number;
  line: number;
  column: number;
}

export function validateAndParseJSON(jsonString: string, allowJSON5: boolean = false): JSONValidationResult {
  if (!jsonString.trim()) {
    return { isValid: false, error: 'Empty JSON input' };
  }

  try {
    const parsed = allowJSON5 ? JSON5.parse(jsonString) : JSON.parse(jsonString);
    return { isValid: true, parsed };
  } catch (error) {
    if (error instanceof Error) {
      const errorMessage = error.message;
      const lineMatch = errorMessage.match(/line (\d+)/i);
      const columnMatch = errorMessage.match(/column (\d+)/i);
      const positionMatch = errorMessage.match(/position (\d+)/i);
      
      let line = lineMatch ? parseInt(lineMatch[1]) : undefined;
      let column = columnMatch ? parseInt(columnMatch[1]) : undefined;
      
      // If we have position but not line/column, calculate them
      if (positionMatch && !line && !column) {
        const position = parseInt(positionMatch[1]);
        const lines = jsonString.substring(0, position).split('\n');
        line = lines.length;
        column = lines[lines.length - 1].length + 1;
      }

      const suggestion = generateSuggestion(errorMessage, jsonString, line, column);

      return {
        isValid: false,
        error: errorMessage,
        line,
        column,
        suggestion
      };
    }
    return { isValid: false, error: 'Invalid JSON format' };
  }
}

export function formatJSON(jsonString: string, allowJSON5: boolean = false): string {
  try {
    const parsed = allowJSON5 ? JSON5.parse(jsonString) : JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (error) {
    throw new Error('Cannot format invalid JSON');
  }
}

export function findJSONPath(jsonString: string, path: string): HighlightRange | null {
  try {
    JSON.parse(jsonString); // Validate JSON
    const pathParts = parsePath(path);
    
    if (!pathParts.length) {
      // Root selection - highlight entire JSON
      return {
        start: 0,
        end: jsonString.length,
        line: 1,
        column: 1
      };
    }

    return findPathInJSON(jsonString, pathParts);
  } catch (error) {
    return null;
  }
}

function parsePath(path: string): string[] {
  if (!path) return [];
  
  const parts: string[] = [];
  let current = '';
  let inBrackets = false;
  
  for (let i = 0; i < path.length; i++) {
    const char = path[i];
    
    if (char === '[') {
      if (current) {
        parts.push(current);
        current = '';
      }
      inBrackets = true;
    } else if (char === ']') {
      if (inBrackets && current) {
        parts.push(current);
        current = '';
      }
      inBrackets = false;
    } else if (char === '.' && !inBrackets) {
      if (current) {
        parts.push(current);
        current = '';
      }
    } else {
      current += char;
    }
  }
  
  if (current) {
    parts.push(current);
  }
  
  return parts;
}

function findPathInJSON(jsonString: string, pathParts: string[]): HighlightRange | null {
  try {
    // Validate that the path exists in the JSON
    const parsed = JSON.parse(jsonString);
    let current = parsed;
    
    for (const part of pathParts) {
      if (Array.isArray(current)) {
        const index = parseInt(part);
        if (isNaN(index) || index < 0 || index >= current.length) {
          return null;
        }
        current = current[index];
      } else if (typeof current === 'object' && current !== null) {
        if (!(part in current)) {
          return null;
        }
        current = current[part];
      } else {
        return null;
      }
    }
    
    // Find the line that contains this specific path
    const lines = jsonString.split('\n');
    let foundLine = -1;
    
    if (pathParts.length === 0) {
      return {
        start: 0,
        end: jsonString.length,
        line: 1,
        column: 1
      };
    }
    
    // Simple but effective approach: look for the last part of the path
    const lastPart = pathParts[pathParts.length - 1];
    const isArrayIndex = /^\d+$/.test(lastPart);
    
    if (!isArrayIndex) {
      // Looking for an object property
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        // Match the property name with colon, ensuring it's a key not a value
        const keyPattern = new RegExp(`^\\s*"${escapeRegExp(lastPart)}"\\s*:`);
        if (keyPattern.test(line)) {
          foundLine = i;
          break;
        }
      }
    } else {
      // Looking for an array element - this is more complex
      // For now, find any line that could contain the array value
      const arrayIndex = parseInt(lastPart);
      let currentArrayIndex = 0;
      let inArray = false;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (line.includes('[')) {
          inArray = true;
          currentArrayIndex = 0;
          // Check if value is on same line as opening bracket
          const afterBracket = line.substring(line.indexOf('[') + 1).trim();
          if (afterBracket && !afterBracket.startsWith(']') && arrayIndex === 0) {
            foundLine = i;
            break;
          }
        } else if (inArray && line.includes(']')) {
          inArray = false;
        } else if (inArray) {
          if (currentArrayIndex === arrayIndex && line !== '' && !line.startsWith('}') && !line.startsWith(']')) {
            foundLine = i;
            break;
          }
          if (line.includes(',')) {
            currentArrayIndex++;
          }
        }
      }
    }
    
    if (foundLine >= 0) {
      const line = lines[foundLine];
      const startOffset = getStringOffset(jsonString, foundLine, 0);
      const endOffset = startOffset + line.length;
      
      return {
        start: startOffset,
        end: endOffset,
        line: foundLine + 1,
        column: 1
      };
    }
    
    return null;
  } catch (error) {
    return null;
  }
}


function getStringOffset(text: string, line: number, column: number): number {
  const lines = text.split('\n');
  let offset = 0;
  
  for (let i = 0; i < line && i < lines.length; i++) {
    offset += lines[i].length + 1; // +1 for newline character
  }
  
  return offset + column;
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function generateSuggestion(errorMessage: string, jsonString: string, line?: number, column?: number): string {
  const lowerError = errorMessage.toLowerCase();
  
  if (lowerError.includes('unexpected token')) {
    if (lowerError.includes("'")) {
      return 'Use double quotes instead of single quotes for strings';
    }
    if (lowerError.includes('unexpected end')) {
      return 'Missing closing bracket or brace';
    }
    return 'Check for missing commas, quotes, or brackets';
  }
  
  if (lowerError.includes('expected')) {
    return 'Check for missing or extra commas';
  }
  
  if (lowerError.includes('duplicate')) {
    return 'Remove duplicate keys in the object';
  }
  
  if (lowerError.includes('trailing comma')) {
    return 'Remove trailing commas (not allowed in JSON)';
  }
  
  return 'Check JSON syntax - ensure proper quotes, commas, and brackets';
}